/**
 * Mentions and replies - thin typed client over the three mention routes (plan B3.3;
 * backend/src/routes/crmMentions.js: GET /mentionable, GET /mentions, POST /mentions/read) and over
 * the note route's two new fields (POST /api/crm/contacts/:id/notes with `mentions` and
 * `reply_to_id`, routes/crm.js), in the layout of recruit/api.ts. Every answer is read through
 * mentionLogic's normalizers, so a component never meets a half-shaped row.
 *
 * No poll and no stream here (plan B0): the unread count and the newest mention ride the ONE count
 * poll, GET /worklist?counts=true (useNavCounts.ts). A caller that marks mentions read follows a
 * successful markMentionsRead with notifyStatsChanged() (../lib), which refreshes that poll at once,
 * so the Today badge, the lane and the card clear together.
 */
import api from '../../services/api';
import type { Communication } from '../types';
import {
  MENTION_LIST_LIMIT_MAX,
  MENTION_READ_IDS_MAX,
  cleanIds,
  mentionItemOf,
  mentionableUsersOf,
} from './mentionLogic';
import type {
  MentionItem,
  MentionListParams,
  MentionReadRequest,
  MentionReadResponse,
  MentionableUser,
  MentionsResponse,
  NoteCreateRequest,
  NoteCreateResponse,
} from './types';

const BASE = '/api/crm';

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

/** The plain sentence to show for a failed call: the server's when it sent one, never a raw status. */
export function mentionErrorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const res = (e as any)?.response;
  const data = res?.data;
  if (data && typeof data.error === 'string' && data.error.trim()) return data.error.trim();
  if (res?.status === 401 || res?.status === 403) return "You don't have access to that.";
  if (res?.status === 404) return "That wasn't found. Refresh the page and try again.";
  if (!res || res.status === 502 || res.status === 503) {
    return "Mentions aren't available right now. Please try again in a moment.";
  }
  return fallback;
}

/**
 * True when this api does not serve mentions yet (an older deploy: 404) or is down (502, 503, no
 * answer): the lane, the page and the card hide rather than show an error (the recruit rule).
 */
export function isMentionsUnavailable(e: unknown): boolean {
  const status = (e as any)?.response?.status;
  return status === 404 || status === 502 || status === 503 || !(e as any)?.response;
}

// ---------------------------------------------------------------------------------------------
// Who can be mentioned: fetched once per session and shared (lib.ts fetchTeam's pattern)
// ---------------------------------------------------------------------------------------------

let mentionableCache: MentionableUser[] | null = null;
let mentionablePromise: Promise<MentionableUser[]> | null = null;
// Bumped by clearMentionableCache, so a request already in flight cannot refill the cache with a
// list read before the clear.
let mentionableGeneration = 0;

/**
 * GET /mentionable: every teammate who can be mentioned (decision M2 a), sorted by name. Read once
 * per session and shared: concurrent callers share one request, a failure REJECTS (the caller
 * shows it) and lets the next call try again, and an empty answer is not kept.
 */
export function listMentionable(): Promise<MentionableUser[]> {
  if (mentionableCache) return Promise.resolve(mentionableCache);
  if (!mentionablePromise) {
    const generation = mentionableGeneration;
    mentionablePromise = api.get(`${BASE}/mentionable`).then(
      (res) => {
        const users = mentionableUsersOf(res?.data?.users);
        if (generation === mentionableGeneration) {
          if (users.length) mentionableCache = users;
          mentionablePromise = null;
        }
        return users;
      },
      (err) => {
        if (generation === mentionableGeneration) mentionablePromise = null;
        throw err;
      },
    );
  }
  return mentionablePromise;
}

/** Forget the cached teammates: the next listMentionable() reads them again. */
export function clearMentionableCache(): void {
  mentionableGeneration += 1;
  mentionableCache = null;
  mentionablePromise = null;
}

// ---------------------------------------------------------------------------------------------
// The viewer's mentions (GET /mentions) and marking them read (POST /mentions/read)
// ---------------------------------------------------------------------------------------------

/**
 * GET /mentions: the viewer's mentions, newest first. `status` unread (the default the server
 * applies) or all; `limit` is clamped to 1..50; `before` (the last row's created_at) pages back. A
 * row without the ids it needs to open is dropped.
 */
export async function listMentions(params: MentionListParams = {}): Promise<MentionsResponse> {
  const query: Record<string, string | number> = {};
  if (params.status === 'unread' || params.status === 'all') query.status = params.status;
  if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
    query.limit = Math.min(MENTION_LIST_LIMIT_MAX, Math.max(1, Math.floor(params.limit)));
  }
  const before = typeof params.before === 'string' ? params.before.trim() : '';
  if (before) query.before = before;
  const { data } = await api.get(`${BASE}/mentions`, { params: query });
  const mentions: MentionItem[] = [];
  for (const row of Array.isArray(data?.mentions) ? data.mentions : []) {
    const item = mentionItemOf(row);
    if (item) mentions.push(item);
  }
  return { mentions, has_more: data?.has_more === true };
}

async function postRead(body: MentionReadRequest): Promise<number> {
  const { data } = await api.post(`${BASE}/mentions/read`, body);
  const n = data?.updated;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * POST /mentions/read with exactly one of { ids }, { contact_id } or { all: true } (plan B3.3; the
 * server stamps only the caller's own rows). Ids that are not UUIDs are dropped and more than 50
 * go in batches of 50; nothing to mark (no ids, a contact id that is not a UUID) sends no request
 * and answers 0. Then call notifyStatsChanged() so the badge, the lane and the card clear.
 */
export async function markMentionsRead(req: MentionReadRequest): Promise<MentionReadResponse> {
  const r = (req || {}) as { ids?: unknown; contact_id?: unknown; all?: unknown };
  if (Array.isArray(r.ids)) {
    const ids = cleanIds(r.ids);
    let updated = 0;
    for (let i = 0; i < ids.length; i += MENTION_READ_IDS_MAX) {
      updated += await postRead({ ids: ids.slice(i, i + MENTION_READ_IDS_MAX) });
    }
    return { updated };
  }
  if (typeof r.contact_id === 'string') {
    const [contactId] = cleanIds([r.contact_id]);
    return { updated: contactId ? await postRead({ contact_id: contactId }) : 0 };
  }
  if (r.all === true) return { updated: await postRead({ all: true }) };
  return { updated: 0 };
}

// ---------------------------------------------------------------------------------------------
// Writing a note that mentions or replies (the composer, the call-mode scratchpad, Reply)
// ---------------------------------------------------------------------------------------------

const noteOf = (v: unknown): Communication | null =>
  v && typeof v === 'object' && typeof (v as Communication).id === 'string' ? (v as Communication) : null;

/**
 * POST /contacts/:id/notes (plan B3.3). `mentions` (extractMentions at Post; junk dropped, each id
 * once) and `reply_to_id` are sent only when there are any, so a plain note is exactly today's
 * `{ body }`. The note is saved first: `mentions.failed` true means it is saved but nobody could be
 * told (mentionLogic notifyFailedWords says so). A refusal (400 with the unknown ids, 404, 409)
 * rejects like any call: show mentionErrorMessage(e).
 */
export async function createNote(contactId: string, req: NoteCreateRequest): Promise<NoteCreateResponse> {
  const body: NoteCreateRequest = { body: req.body };
  const mentions = cleanIds(req.mentions);
  if (mentions.length) body.mentions = mentions;
  const replyTo = typeof req.reply_to_id === 'string' ? req.reply_to_id.trim() : '';
  if (replyTo) body.reply_to_id = replyTo;
  const { data } = await api.post(`${BASE}/contacts/${encodeURIComponent(contactId)}/notes`, body);
  return {
    ok: data?.ok !== false,
    communication: noteOf(data?.communication),
    mentions: { failed: data?.mentions?.failed === true },
  };
}
